import { formatISO, subDays } from 'date-fns';
import { useMemo } from 'react';

export type SuggestionId =
    | 'urgent-followups'
    | 'hot-leads'
    | 'dormant-customers'
    | 'high-value-prospects'
    | 'churn-risk-customers';

export interface IntentBasedFilter {
    networking_objective_weight: number;
    sales_objective_weight: number;
    customer_success_objective_weight: number;
    fundraising_objective_weight: number;
    recruiting_objective_weight: number;
    internal_objective_weight: number;
    timing_urgency_max_boost: number;
    urgent_followup_boost: number;
    no_action_penalty: number;
    shelf_base_boost: number;
    shelf_period_days: number;
    very_old_overdue_base_boost: number;
    very_old_decay_rate: number;
    due_today_boost: number;
    due_curve_width: number;
    future_penalty: number;
    future_decay_rate: number;
    order_by: Array<{ compositeScore: string }>;
    limit: number;
    offset: number;
    where: any;
}

export const useIntentBasedFilters = () => {
    const getFilterForSuggestion = useMemo(() => {
        const baseFilter = {
            networking_objective_weight: 1,
            sales_objective_weight: 1,
            customer_success_objective_weight: 1,
            fundraising_objective_weight: 1,
            recruiting_objective_weight: 1,
            internal_objective_weight: 1,
            timing_urgency_max_boost: 1,
            urgent_followup_boost: 2,
            no_action_penalty: 0.05,
            shelf_base_boost: 1,
            shelf_period_days: 14,
            very_old_overdue_base_boost: 0.9,
            very_old_decay_rate: 3,
            due_today_boost: 1.5,
            due_curve_width: 1.5,
            future_penalty: 0.984,
            future_decay_rate: 0.5,
            order_by: [{ compositeScore: 'desc' }],
            limit: 10,
            offset: 0,
        };

        return (suggestionId: SuggestionId): IntentBasedFilter => {
            switch (suggestionId) {
                case 'urgent-followups':
                    return {
                        ...baseFilter,
                        where: {
                            _and: [
                                {
                                    metadata: {
                                        _contains: {
                                            urgentFollowUp: true,
                                        },
                                    },
                                },
                                {
                                    userSetAsNotInterestedAt: {
                                        _is_null: true,
                                    },
                                },
                                {
                                    lastContactedAt: {
                                        _gte: formatISO(subDays(new Date(), 30)),
                                    },
                                },
                            ],
                        },
                    };

                case 'hot-leads':
                    return {
                        ...baseFilter,
                        where: {
                            _and: [
                                {
                                    _or: [
                                        {
                                            metadata: {
                                                _contains: {
                                                    buyingIntent: 'VERY_HIGH',
                                                },
                                            },
                                        },
                                        {
                                            metadata: {
                                                _contains: {
                                                    buyingIntent: 'HIGH',
                                                },
                                            },
                                        },
                                    ],
                                },
                                {
                                    metadata: {
                                        _contains: {
                                            contactType: 'PROSPECT',
                                        },
                                    },
                                },
                                {
                                    userSetAsNotInterestedAt: {
                                        _is_null: true,
                                    },
                                },
                                {
                                    lastContactedAt: {
                                        _gte: formatISO(subDays(new Date(), 30)),
                                    },
                                },
                            ],
                        },
                    };

                case 'dormant-customers':
                    return {
                        ...baseFilter,
                        where: {
                            _and: [
                                {
                                    metadata: {
                                        _contains: {
                                            engagementLevel: 'DORMANT',
                                        },
                                    },
                                },
                                {
                                    metadata: {
                                        _contains: {
                                            contactType: 'CUSTOMER',
                                        },
                                    },
                                },
                                {
                                    userSetAsNotInterestedAt: {
                                        _is_null: true,
                                    },
                                },
                                {
                                    lastContactedAt: {
                                        _lte: formatISO(subDays(new Date(), 90)),
                                    },
                                },
                            ],
                        },
                    };

                case 'churn-risk-customers':
                    return {
                        ...baseFilter,
                        where: {
                            _and: [
                                {
                                    metadata: {
                                        _contains: {
                                            riskFactors: ['CUSTOMER_CHURN_RISK'],
                                        },
                                    },
                                },
                                {
                                    metadata: {
                                        _contains: {
                                            contactType: 'CUSTOMER',
                                        },
                                    },
                                },
                                {
                                    userSetAsNotInterestedAt: {
                                        _is_null: true,
                                    },
                                },
                                {
                                    lastContactedAt: {
                                        _gte: formatISO(subDays(new Date(), 90)),
                                    },
                                },
                            ],
                        },
                    };

                case 'high-value-prospects':
                    return {
                        ...baseFilter,
                        where: {
                            _and: [
                                {
                                    _or: [
                                        {
                                            metadata: {
                                                _contains: {
                                                    businessValue: 'VERY_HIGH',
                                                },
                                            },
                                        },
                                        {
                                            metadata: {
                                                _contains: {
                                                    businessValue: 'HIGH',
                                                },
                                            },
                                        },
                                    ],
                                },
                                {
                                    metadata: {
                                        _contains: {
                                            contactType: 'PROSPECT',
                                        },
                                    },
                                },
                                {
                                    userSetAsNotInterestedAt: {
                                        _is_null: true,
                                    },
                                },
                            ],
                        },
                    };
            }
        };
    }, []);

    return { getFilterForSuggestion };
};
